import { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Card } from '@iris/react-components/Card';
import { ArrowTrendingUpIcon } from '@iris/react-icons/ArrowTrendingUpIcon';
import { BellIcon } from '@iris/react-icons/BellIcon';
import { CarTagIcon } from '@iris/react-icons/CarTagIcon';
import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/core/device';

import { Image, SeeMore } from 'reaxl';

import remoteActions from '@/actions/vdp/remoteActions';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpAvailableOffersDuck from '@/ducks/vdp/vdpAvailableOffersDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import PricingCard from '@/components/PricingCard';

import FairMarketRangeSheet from './FairMarketRangeSheet';
import IncentivesSheet from './IncentivesSheet';
import PriceAlertSheet from './PriceAlertSheet';
import PriceHistorySheet from './PriceHistorySheet';
import PriceInfoCard from './PriceInfoCard';

/**
 * PriceSectionCard Component
 *
 * @description A card component that displays pricing information and related tools including:
 * - KBB Fair Market Range comparison
 * - Available incentives and special offers
 * - Price history trends
 * - Price alert signup functionality
 *
 * The component adapts its layout based on device size, using a collapsible SeeMore component
 * on mobile devices and full display on desktop.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.emailOwnerProps - Props to pass to email-related components for lead generation
 * @param {Function} props.getIncentiveOffers - Function to fetch incentive offers data
 * @param {boolean} props.enabledAmdIncentivesOffers - Flag to enable AMD (Advanced Market Data) incentives offers
 *
 */
export default function PriceSectionCard({
    emailOwnerProps,
    getIncentiveOffers,
    enabledAmdIncentivesOffers,
}) {
    const device = useDevice();
    const dispatch = useDispatch();
    const isXs = _get(device, 'is.xs', false);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const payments = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const rawListingIncentives = useSelector(paymentsDuck.selectors.getListingIncentives);
    const listingIncentives = useMemo(() => rawListingIncentives || {}, [rawListingIncentives]);
    const { availableOffers } = useSelector(vdpAvailableOffersDuck.selectors.getDuckState);

    const { id, owner = {}, pricingHistory = [], hasSpecialOffer } = listing;
    const { privateSeller: isPrivateSeller } = owner;
    const { dealType } = payments;

    const listingIncentivesList = listingIncentives[id]?.[dealType];

    const [isIncentivesSheetOpen, setIsIncentivesSheetOpen] = useState(false);

    const openIncentivesSheet = useCallback(() => {
        if (hasSpecialOffer) {
            dispatch(remoteActions.getAvailableOffers({ id }));
        }
        if (enabledAmdIncentivesOffers) {
            getIncentiveOffers();
        }
        setIsIncentivesSheetOpen(true);
    }, [hasSpecialOffer, enabledAmdIncentivesOffers, dispatch, id, getIncentiveOffers]);

    const getAvailableCards = useCallback(() => {
        const cards = [];

        if (listing) {
            cards.push({
                icon: (
                    <Image
                        src="/cm-api/content/static/img/icon/logos/kbb-card-logo.svg"
                        alt="KBB Fair Market Range Logo"
                        data-cmp="kbbCardLogoIcon"
                        height="32px"
                        width="32px"
                    />
                ),
                title: isPrivateSeller
                    ? 'Kelley Blue Book® Private Party Range'
                    : 'Kelley Blue Book® Fair Market Range',
                description: 'See how the price compares to what others have been paying.',
                highlightedText: 'See how the price compares',
                dataCmp: 'fairMarketRangeCard',
                SheetComponent: FairMarketRangeSheet,
                sheetProps: { isPrivateSeller, listing, owner },
            });
        }

        if (hasSpecialOffer || enabledAmdIncentivesOffers) {
            cards.push({
                icon: <CarTagIcon
                    data-cmp="carTagIcon"
                    className="iris:size-[32px]"
                />,
                title: 'More ways to save',
                description: 'Explore all specials to see if you qualify.',
                highlightedText: 'Explore all specials',
                dataCmp: 'incentiveCard',
                onClick: openIncentivesSheet,
                SheetComponent: IncentivesSheet,
                sheetProps: {
                    isXs,
                    availableOffers: availableOffers || listingIncentivesList,
                    emailOwnerProps,
                    isIncentivesSheetOpen,
                    setIsIncentivesSheetOpen,
                },
            });
        }

        if (pricingHistory && pricingHistory.length > 0) {
            cards.push({
                icon: <ArrowTrendingUpIcon
                    data-cmp="arrowTrendingUpIcon"
                    className="iris:size-[32px]"
                />,
                title: 'Explore pricing trends',
                description: 'Take a look at how the price changed over time.',
                highlightedText: 'how the price changed',
                dataCmp: 'priceHistoryCard',
                SheetComponent: PriceHistorySheet,
                sheetProps: { pricingHistory },
            });
        }

        if (!isPrivateSeller) {
            cards.push({
                icon: <BellIcon
                    data-cmp="bellIcon"
                    className="iris:size-[32px]"
                />,
                title: 'Sign up for price alerts',
                description: 'Get notified about price changes for this vehicle.',
                highlightedText: 'Get notified',
                dataCmp: 'priceAlertCard',
                SheetComponent: PriceAlertSheet,
                sheetProps: {},
            });
        }

        return cards;
    }, [
        listing,
        owner,
        isPrivateSeller,
        hasSpecialOffer,
        availableOffers,
        openIncentivesSheet,
        pricingHistory,
        listingIncentivesList,
        enabledAmdIncentivesOffers,
        isXs,
        emailOwnerProps,
        isIncentivesSheetOpen,
        setIsIncentivesSheetOpen,
    ]);

    const availableCards = getAvailableCards();
    const cardCount = availableCards.length;

    const renderCardElements = availableCards.map((card) => {
        const { SheetComponent, sheetProps, onClick } = card;

        const cardElement = (
            <PriceInfoCard
                key={card.dataCmp}
                icon={card.icon}
                title={card.title}
                description={card.description}
                highlightedText={card.highlightedText}
                dataCmp={card.dataCmp}
                {...(onClick && { onClick })}
            />
        );

        return (
            <SheetComponent
                key={`${card.dataCmp}-sheet`}
                trigger={cardElement}
                className="iris:mt-4 iris:w-full"
                {...sheetProps}
            />
        );
    });

    const enableSeeMoreWrapper = isXs && cardCount > 2;

    return (
        <Card
            variant="brand-subtle"
            className={clsx(
                {
                    'iris:mb-5': !isXs,
                    'iris:mb-1': isXs,
                },
                'iris:h-auto'
            )}
        >
            <Card.Content className={clsx({ 'iris:pb-0': enableSeeMoreWrapper })}>
                <PricingCard uiContext="price-section" />
                {enableSeeMoreWrapper ? (
                    <SeeMore
                        collapsedSize="md"
                        disableFade
                    >
                        {renderCardElements}
                    </SeeMore>
                ) : renderCardElements}
            </Card.Content>
        </Card>
    );
}
